import { useEffect, type RefObject } from 'react';

/**
 * CSS selector for keyboard-focusable elements, in DOM (tab) order. Exported so
 * focus-trap tests can mirror the trap's notion of "focusable" without drifting
 * from it. Still filter the result by `!el.hidden && aria-hidden !== 'true'`.
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Contain keyboard focus within a modal container while it is open (WCAG 2.4.3
 * + the ARIA APG dialog pattern). A `role="dialog" aria-modal="true"` surface
 * MUST trap focus: `aria-modal` tells assistive tech the rest of the page is
 * inert, so the Tab order has to match that promise — otherwise a keyboard user
 * Tabs straight out of the dialog into the page behind it (verified leak on the
 * hand-rolled dashboard modals — focus escaped to the footer links).
 *
 * The canonical `@syncanix/ui` `Dialog` (Radix) already does this; this hook is
 * for the hand-rolled `<div role="dialog">` modals that predate it and can't
 * cheaply migrate to Radix without restructuring their markup.
 *
 * Behaviour while `active`:
 * - `Tab` past the last focusable element wraps to the first.
 * - `Shift+Tab` before the first wraps to the last.
 * - If focus ever lands outside the container (e.g. a programmatic blur), the
 *   next `Tab` pulls it back to the first focusable element inside.
 *
 * It does NOT own initial focus, Escape handling, or focus restoration — the
 * consumer already manages those (and they're orthogonal concerns).
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (container === null) return;

    function focusable(): HTMLElement[] {
      if (container === null) return [];
      // The dialogs reveal/hide fields by mounting/unmounting (not display:none),
      // so an element in the DOM is genuinely tabbable. The `hidden` attribute is
      // the one in-DOM-but-not-focusable case worth excluding.
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hidden && el.getAttribute('aria-hidden') !== 'true',
      );
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Tab' || container === null) return;
      const elements = focusable();
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (first === undefined || last === undefined) {
        // Nothing tabbable inside — keep focus on the container itself rather
        // than letting it escape to the page behind the modal.
        event.preventDefault();
        return;
      }
      const activeEl = document.activeElement;

      if (!container.contains(activeEl)) {
        // Focus is outside the trap; pull it back in.
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey) {
        if (activeEl === first) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [containerRef, active]);
}
